/**
 * Source-agnostic semantic field registry.
 *
 * C-ORE's frontend reads SEMANTIC fields (`pipeline`, `nps`, `job_costing`,
 * `contacts`, etc.) — never source-named fields (`tally_revenue`,
 * `zoho_pipeline`). Any present or future connector populates the same
 * semantic field and every panel works unchanged.
 *
 * Connectors active today: Tally
 * Connectors planned (roadmap): Zoho, Razorpay, GST portal, Odoo, Excel
 * upload, Salesforce, Oracle, NetSuite, C-ORE's own CRM.
 *
 * This module exposes two helpers + a small connector hint map so empty
 * states can tell the customer exactly which connector unlocks the
 * panel.
 */

/**
 * Walk a dotted/array path on a dashboard payload. Treats empty arrays,
 * empty strings, null, undefined, 0-length objects as "no data".
 *
 * @param {object} dashboardData — full `/api/customer/dashboard-data` payload
 * @param {string} fieldPath — e.g. "debtors.debtors_raw", "ceo.client_concentration_pct"
 * @returns {boolean}
 */
export function hasSemanticData(dashboardData, fieldPath) {
  if (!dashboardData || !fieldPath) return false;
  const parts = fieldPath.split(".");
  let cur = dashboardData;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return false;
    cur = cur[p];
  }
  if (cur == null) return false;
  if (Array.isArray(cur)) return cur.length > 0;
  if (typeof cur === "string") return cur.trim().length > 0;
  if (typeof cur === "object") return Object.keys(cur).length > 0;
  if (typeof cur === "number") return Number.isFinite(cur);
  return Boolean(cur);
}

/**
 * Return the dotted-path value (no truthiness coercion). Callers use this
 * after `hasSemanticData` confirms the value exists.
 */
export function readSemanticField(dashboardData, fieldPath, fallback = null) {
  if (!dashboardData || !fieldPath) return fallback;
  const parts = fieldPath.split(".");
  let cur = dashboardData;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return fallback;
    cur = cur[p];
  }
  return cur == null ? fallback : cur;
}

/**
 * Connector hints per semantic field. Used by `ConnectSourceEmptyState`
 * so the empty-state CTA names the right integration to wire up. Each
 * entry lists the connectors C-ORE supports / plans to support that
 * populate that field — order is "first one shipping" → "later options".
 */
export const SEMANTIC_FIELD_CONNECTORS = {
  // Already-live in Tally connector (covered by debtors.debtors_raw[],
  // financial_snapshots, etc.). Empty here means the customer hasn't
  // synced their Tally yet — empty-state CTA points to Tally.
  "debtors.debtors_raw":      { connectors: ["Tally"],                        copy: "outstanding-debtor data" },
  "debtors.critical_debtors": { connectors: ["Tally"],                        copy: "overdue receivables" },

  // Sales pipeline / open opportunities — needs CRM
  "pipeline.open_opportunities": { connectors: ["Zoho CRM", "Salesforce", "C-ORE CRM (coming soon)"], copy: "sales pipeline" },
  "pipeline.won_deals":          { connectors: ["Zoho CRM", "Salesforce", "C-ORE CRM (coming soon)"], copy: "closed-won deal history" },

  // Job-card / per-job cost variance — Odoo, future Tally TDL, Excel upload
  "job_costing.jobs": { connectors: ["Odoo", "Excel upload", "Tally (extended TDL — coming soon)"], copy: "per-job cost variance" },

  // Customer-relationship enrichment (contact, last-order-date, lifetime
  // revenue per customer) — Tally party masters (future TDL), Zoho.
  "customers.portfolio": { connectors: ["Tally (extended TDL — coming soon)", "Zoho CRM", "Salesforce"], copy: "customer relationships (contact, last-order, lifetime value)" },

  // Forecasting demand signals — future C-ORE forecast model + CRM
  "forecast.demand_signals": { connectors: ["C-ORE forecasting (coming soon)", "Zoho CRM", "Salesforce"], copy: "forward demand signals" },
  "forecast.cash_dips":       { connectors: ["C-ORE forecasting (coming soon)"],                          copy: "forward cash-dip projections" },
};

/**
 * @returns {{connectors: string[], copy: string} | null}
 */
export function suggestedConnectorFor(fieldPath) {
  return SEMANTIC_FIELD_CONNECTORS[fieldPath] || null;
}
